import { useCallback, useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import {
  AllDoneFill,
  ArrowBackOutline,
  Certificate2Icon,
  EditOutline,
  GridOutline,
  RocketOutline,
} from '@lyearn/icons';
import { cn, css } from '@lyearn/style';
import { Button, Loader, Stack, Typography } from '@lyearn/ui';
import usePageLayout from '@/components/PageLayout/usePageLayout';
import useNavigation from '@/hooks/useNavigation';
import { useQuestionsQuery } from '@/modules/corporate/modules/questions/views/AddQuestionModal/graphql/queries/generated/questions';
import TemplatesHeaderNode from '@/modules/templates/views/components/TemplatesHeaderNode';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import {
  ContentApprovalStatusEnum,
  KnowledgeAssetContentType,
  ResourceStatusEnum,
  UserSurveyResponseVersionStatusEnum,
} from '@/types/schema';

import { useKnowledgeAssetsQuery } from '../../KnowledgeAssetManager/graphql/query/generated/knowledgeAssets';
import { usePersonaQuery } from '../graphql/queries/generated/persona';
import { usePersonaDetailsQuery } from '../graphql/queries/generated/personaDetails';
import { usePersonaQuestionIdsQuery } from '../graphql/queries/generated/personaQuestionIds';
import useApproveContent from '../hooks/useApproveContent';
import { DetailsPageProps } from '../types';
import CXArchitecturePage from './tabs/CXArchitecturePage';
import KeyDifferentiatorsPage from './tabs/KeyDifferentiatorsPage';
import OverviewPage from './tabs/OverviewPage';
import PersonaJourneyPage from './tabs/PersonaJourneyPage';
import POVTrendPage from './tabs/POVTrendPage';
import { parsePipeSeparatedString } from './utils';
import { getResponseByQuestionText } from './utils';

export enum SolutionTab {
  OVERVIEW = 'overview',
  POV_TREND = 'pov_trend',
  PERSONA_JOURNEY = 'persona_journey',
  CX_ARCHITECTURE = 'cx_architecture',
  KEY_DIFFERENTIATORS = 'key_differentiators',
}

const TAB_LABELS: Record<SolutionTab, string> = {
  [SolutionTab.OVERVIEW]: 'Overview',
  [SolutionTab.POV_TREND]: 'POV/Trend',
  [SolutionTab.PERSONA_JOURNEY]: 'Persona & Journey',
  [SolutionTab.CX_ARCHITECTURE]: 'CX Architecture',
  [SolutionTab.KEY_DIFFERENTIATORS]: 'Key Differentiators',
};

const getTabIcon = (tab: SolutionTab, isSelected: boolean) => {
  const iconProps = {
    height: 20,
    width: 20,
    fillColorToken: isSelected ? 'icon-active' : 'icon-secondary',
  };

  switch (tab) {
    case SolutionTab.OVERVIEW:
      return <RocketOutline {...iconProps} />;
    case SolutionTab.POV_TREND:
      return <GridOutline {...iconProps} />;
    default:
      return <Certificate2Icon {...iconProps} />;
  }
};

const SolutionDetails = ({ assetId, isPreview }: DetailsPageProps) => {
  const { isMobile } = useScreen();
  const [currentTab, setCurrentTab] = useState<SolutionTab>(SolutionTab.OVERVIEW);
  const isAdminRoute = useAdminRoute();
  const navigation = useNavigation();

  const [{ data: personaData, fetching: fetchingPersonaData }] = usePersonaQuery({
    variables: {
      filters: {
        id: assetId,
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const isDraft =
    (personaData?.content as KnowledgeAssetContentType)?.status === ResourceStatusEnum.Draft;

  const { onApprove, fetching: isApproving } = useApproveContent({ contentId: assetId });

  const showApproveButton =
    (personaData?.content as KnowledgeAssetContentType)?.approvalStatus ===
      ContentApprovalStatusEnum.InReview &&
    (personaData?.content as KnowledgeAssetContentType)?.status === ResourceStatusEnum.Published;

  const attemptIds =
    (personaData?.content as any)?.userSurveyAttemptConfig?.map(
      (attempt: any) => attempt?.attemptId,
    ) || [];

  const userSurveyIds = (personaData?.content as any)?.userSurveyAttemptConfig?.map(
    (attempt: any) => attempt?.userSurveyId,
  );

  const [{ data: personaDetailsData, fetching: fetchingPersonaDetailsData }] =
    usePersonaDetailsQuery({
      variables: {
        filters: {
          attemptIds,
          ...(isPreview ? { versionStatus: UserSurveyResponseVersionStatusEnum.Draft } : {}),
        },
        first: -1,
      },
      pause: !attemptIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const [{ data: personaQuestionsData, fetching: fetchingUserSurveyDetailsData }] =
    usePersonaQuestionIdsQuery({
      variables: {
        filters: {
          ids: userSurveyIds,
        },
      },
      pause: !userSurveyIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const questionIds =
    personaQuestionsData?.userSurveys?.edges?.flatMap(
      (edge) =>
        edge?.node?.questionConfig?.surveyQuestions?.map(
          (question) => question?.questionId || '',
        ) || [],
    ) || [];

  const [{ data: questionsData, fetching: fetchingQuestionsData }] = useQuestionsQuery({
    variables: {
      filters: {
        ids: questionIds,
      },
    },
    pause: !questionIds?.length,
    requestPolicy: 'cache-and-network',
  });

  const solutionName = getResponseByQuestionText(
    'Solution Name',
    questionsData,
    personaDetailsData,
  );
  const description = getResponseByQuestionText('Description', questionsData, personaDetailsData);
  const highlights = parsePipeSeparatedString(
    getResponseByQuestionText('Solution Highlights', questionsData, personaDetailsData),
  );
  const painPoints = parsePipeSeparatedString(
    getResponseByQuestionText('Pain Points', questionsData, personaDetailsData),
  );
  const trendsAndPOV = parsePipeSeparatedString(
    getResponseByQuestionText('POV', questionsData, personaDetailsData),
  );

  const practitionerIds = getResponseByQuestionText(
    'Practitioner',
    questionsData,
    personaDetailsData,
  )
    .split(',')
    .map((id) => id.trim());
  const executiveBuyerIds = getResponseByQuestionText(
    'Executive Buyer',
    questionsData,
    personaDetailsData,
  )
    .split(',')
    .map((id) => id.trim());
  const decisionMakerIds = getResponseByQuestionText(
    'Decision Maker',
    questionsData,
    personaDetailsData,
  )
    .split(',')
    .map((id) => id.trim());

  const platformCapabilities = parsePipeSeparatedString(
    getResponseByQuestionText('Platform Capabilities', questionsData, personaDetailsData),
  );
  const productCapabilities = parsePipeSeparatedString(
    getResponseByQuestionText('Product Capabilities', questionsData, personaDetailsData),
  );
  const typicalIntegrations = parsePipeSeparatedString(
    getResponseByQuestionText('Typical Integrations', questionsData, personaDetailsData),
  );

  const differentiators = parsePipeSeparatedString(
    getResponseByQuestionText('Differentiators', questionsData, personaDetailsData),
  );
  const successKeyMetrics = parsePipeSeparatedString(
    getResponseByQuestionText('Success Key Metrics', questionsData, personaDetailsData),
  );
  const competitors = parsePipeSeparatedString(
    getResponseByQuestionText('Competitors', questionsData, personaDetailsData),
  );

  const integrationLandscapes = parsePipeSeparatedString(
    getResponseByQuestionText('Integration Landscapes', questionsData, personaDetailsData),
  );
  const platformCommonGrounds = parsePipeSeparatedString(
    getResponseByQuestionText('Platform Common Grounds', questionsData, personaDetailsData),
  );
  const competitiveLandscape = parsePipeSeparatedString(
    getResponseByQuestionText('Competitive Landscape', questionsData, personaDetailsData),
  );
  const appToolsFunctions = parsePipeSeparatedString(
    getResponseByQuestionText('App/Tools/Functions', questionsData, personaDetailsData),
  );

  const marketChallenges = parsePipeSeparatedString(
    getResponseByQuestionText('Market Challenges', questionsData, personaDetailsData),
  );

  const businessOutcomes = parsePipeSeparatedString(
    getResponseByQuestionText('Business Outcomes', questionsData, personaDetailsData),
  );

  const links = parsePipeSeparatedString(
    getResponseByQuestionText('URL', questionsData, personaDetailsData),
  );

  const allPersonaIds = [...practitionerIds, ...executiveBuyerIds, ...decisionMakerIds];

  const filteredPersonaIds = allPersonaIds?.filter((id) => Boolean(id)) || [];

  const [{ data: targetPersonasData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: filteredPersonaIds,
      },
    },
    pause: !filteredPersonaIds.length,
    requestPolicy: 'cache-and-network',
  });

  const isLoading =
    fetchingPersonaData ||
    fetchingPersonaDetailsData ||
    fetchingUserSurveyDetailsData ||
    fetchingQuestionsData;

  const pageTitle = (personaData?.content as any)?.name;

  const handleEditClick = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/solution/${assetId}/edit`);
  }, [assetId, navigation]);

  const onBack = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/solution`);
  }, [assetId, navigation]);

  usePageLayout({
    title: pageTitle,
    LeftNode: <TemplatesHeaderNode isPreview={isPreview} title={pageTitle} onBack={onBack} />,
    RightNode:
      isPreview || isMobile ? null : (
        <Stack direction="row" gapX={8}>
          {showApproveButton ? (
            <Button
              StartIcon={AllDoneFill}
              loading={isApproving}
              variant="outlined"
              onClick={onApprove}>
              Approve
            </Button>
          ) : null}
          <Button StartIcon={EditOutline} variant="outlined" onClick={handleEditClick}>
            Edit Solution
          </Button>
        </Stack>
      ),
    showHeader: !isMobile,
  });
  const onbackPress = useCallback(() => {
    // navigation.replace(routes.LibraryPortal(), { appendQuery: false });
    navigation.back();
  }, [navigation]);
  if (isLoading) {
    return (
      <Stack alignItems="center" className="h-[calc(100vh-64px)] w-full" justifyContent="center">
        <Loader size={40} />
      </Stack>
    );
  }

  const renderTabContent = () => {
    const commonProps = {
      solutionName,
      description,
      highlights,
      painPoints,
      trendsAndPOV,
      targetPersonas: {
        practitioners:
          (targetPersonasData?.contents as any)?.edges?.filter((edge: any) =>
            practitionerIds.includes(edge?.node?.id || ''),
          ) || [],
        executiveBuyers:
          (targetPersonasData?.contents as any)?.edges?.filter((edge: any) =>
            executiveBuyerIds.includes(edge?.node?.id || ''),
          ) || [],
        decisionMakers:
          (targetPersonasData?.contents as any)?.edges?.filter((edge: any) =>
            decisionMakerIds.includes(edge?.node?.id || ''),
          ) || [],
      },
      platformCapabilities: platformCapabilities || [],
      productCapabilities: productCapabilities || [],
      typicalIntegrations: typicalIntegrations || [],
      differentiators: differentiators || [],
      successKeyMetrics: successKeyMetrics || [],
      competitors: competitors || [],
      integrationLandscapes: integrationLandscapes || [],
      platformCommonGrounds: platformCommonGrounds || [],
      competitiveLandscape: competitiveLandscape || [],
      appToolsFunctions: appToolsFunctions || [],
      marketChallenges: marketChallenges || [],
      businessOutcomes: businessOutcomes || [],
      links: links || [],
      journeyImage: getResponseByQuestionText(
        'Image of journey',
        questionsData,
        personaDetailsData,
      ),
      blueprintImage: getResponseByQuestionText('Blueprint', questionsData, personaDetailsData),
      personaJourney: getResponseByQuestionText(
        'Solution Journey',
        questionsData,
        personaDetailsData,
      ),
    };

    switch (currentTab) {
      case SolutionTab.OVERVIEW:
        return <OverviewPage {...commonProps} setCurrentTab={setCurrentTab} />;
      case SolutionTab.POV_TREND:
        return <POVTrendPage {...commonProps} />;
      case SolutionTab.PERSONA_JOURNEY:
        return <PersonaJourneyPage {...commonProps} />;
      case SolutionTab.CX_ARCHITECTURE:
        return <CXArchitecturePage {...commonProps} />;
      case SolutionTab.KEY_DIFFERENTIATORS:
        return <KeyDifferentiatorsPage {...commonProps} />;
      default:
        return <OverviewPage {...commonProps} />;
    }
  };

  return (
    <Stack className="h-full w-full xs:pt-12" direction="col">
      {isAdminRoute ? null : (
        <Stack className="w-full pl-32 mb-12 xs:pl-12">
          <ArrowBackOutline className="h-24 w-24 cursor-pointer" onClick={onbackPress} />
        </Stack>
      )}
      <Stack className="h-full w-full" direction="row">
        <Stack
          className="min-w-[20vw] bg-bg-secondary px-8 pt-20 xs:hidden"
          direction="col"
          gapY={8}>
          {Object.values(SolutionTab).map((tab, index) => (
            <>
              <Stack
                key={tab}
                alignItems="center"
                className={cn('rounded-6 hover:bg-bg-hover px-12 py-[9px] cursor-pointer')}
                direction="row"
                gapX={8}
                onClick={() => setCurrentTab(tab)}>
                {getTabIcon(tab, currentTab === tab)}
                <Typography
                  className={currentTab === tab ? '' : 'text-text-secondary'}
                  variant="body-short-02">
                  {TAB_LABELS[tab]}
                </Typography>
              </Stack>
              {index === 0 && <div className="h-[1px] w-full bg-border my-8" />}
            </>
          ))}
        </Stack>
        <div
          className={cn(
            css`
              width: 0.1rem;
              height: undefined;
            `,
            'bg-interactive-secondary-hover',
          )}
        />
        <Stack className="w-full" justifyContent="center">
          {renderTabContent()}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SolutionDetails;
